import type { FontPreset, ThemeMode } from '@blufin/theme';
import { Font } from '@blufin/theme';
import type { Density } from '../../context/ThemeContext/ThemeProvider';

const VALID_DENSITIES: readonly Density[] = ['compact', 'spacious', 'standard'];
const VALID_FONTS: readonly FontPreset[] = Font.ids;
const VALID_MODES: readonly ThemeMode[] = ['dark', 'light', 'system'];

export interface IThemeGuards {
  isDensity(raw: unknown): raw is Density;
  isFont(raw: unknown): raw is FontPreset;
  isThemeMode(raw: unknown): raw is ThemeMode;
}

/** Type guards narrowing untrusted appearance values (localStorage, cloud settings, IPC) to the
 *  canonical `Density` / `FontPreset` / `ThemeMode` unions. Single source of truth for the valid value sets. */
export const ThemeGuards: IThemeGuards = {
  /**
   * Narrow an untrusted value to a valid `Density`.
   * @param raw - The untrusted value to check.
   */
  isDensity: (raw: unknown): raw is Density =>
    typeof raw === 'string' && (VALID_DENSITIES as readonly string[]).includes(raw),
  /**
   * Narrow an untrusted value to a valid `FontPreset`.
   * @param raw - The untrusted value to check.
   */
  isFont: (raw: unknown): raw is FontPreset =>
    typeof raw === 'string' && (VALID_FONTS as readonly string[]).includes(raw),
  /**
   * Narrow an untrusted value to a valid `ThemeMode`.
   * @param raw - The untrusted value to check.
   */
  isThemeMode: (raw: unknown): raw is ThemeMode =>
    typeof raw === 'string' && (VALID_MODES as readonly string[]).includes(raw)
};
